<script setup lang="ts">
/**
 * Lifestyle quiz — full-screen layer (Figma Quizzes › Lifestyle quiz · desktop
 * 3229:105744 / mobile 3229:106519). The answers are the user's own, so no AI mark.
 * Every question is optional and applied as the step is left (R203); × asks first, but
 * nothing is lost (09 note). The questions render from the published definition
 * (`GET /surveys/default-lifestyle-survey/config`, BE-152); the server validates every
 * answer against it and a 422 comes back to the field it names.
 */
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import {
  LIFESTYLE_SURVEY_KEY,
  putSurveyResponse,
  surveyFieldErrors,
  type SurveyV2Config,
} from '@/features/survey/api/surveys'
import { queryCache } from '@/queries'
import { useSurveyDefinitionQuery, useUserLifestyleQuery } from '@/queries/survey'
import { PROFILE_MY_STYLE_ROUTE } from '@/router/routes'
import {
  FairQuizHeader,
  FairQuizLayer,
  FairQuizLeaveDialog,
  FairQuizStep,
  FairQuizSummary,
  FairQuizWelcome,
  settleFairQuizHistory,
  type FairQuizSummaryRow,
} from '@/ui/patterns/FairQuiz'
import { FairInputText } from '@/ui/primitives/FairInputText'
import { FairThemeCard } from '@/ui/primitives/FairThemeCard'
import { useFairToast } from '@/ui/patterns/FairToast'
import { useLifestyleQuizStore } from '../store'
import {
  emptyLifestyleQuizState,
  lifestyleQuizDefinitionFrom,
  lifestyleQuizProgress,
  lifestyleQuizResponseBody,
  lifestyleQuizStateFrom,
  lifestyleQuizSteps,
  lifestyleQuizMayMoveOnAfterFailedSave,
  toggleLifestyleQuizAnswer,
  type LifestyleQuizState,
  type LifestyleQuizStep,
} from '../lifestyle-quiz.data'
import './lifestyle-quiz.css'

const store = useLifestyleQuizStore()
const { t, te, locale } = useI18n()
const toast = useFairToast()
const router = useRouter()
const lifestyleQuery = useUserLifestyleQuery(computed(() => store.isOpen))
const definitionQuery = useSurveyDefinitionQuery<SurveyV2Config>(LIFESTYLE_SURVEY_KEY, computed(() => store.isOpen))

const definition = computed(() =>
  lifestyleQuizDefinitionFrom(definitionQuery.data.value?.config ?? null, String(locale.value || 'en')),
)
const ready = computed(() => definition.value.questions.length > 0)

const state = ref<LifestyleQuizState>(emptyLifestyleQuizState())
const step = ref<LifestyleQuizStep>('welcome')
const steps = computed(() => lifestyleQuizSteps(definition.value.questions, state.value))
const question = computed(() => definition.value.questions.find((item) => item.id === step.value) ?? null)
const firstQuestionId = computed(() => definition.value.questions[0]?.id ?? null)

/** The server's 422 messages by question id (BE-152); cleared on the next answer. */
const fieldErrors = ref<Record<string, string>>({})

/**
 * Applies the answers so far (R203). A refused answer brings the quiz back to its step; a failed
 * save before the Result screen keeps her on the step with the answers and says so (audit #6).
 */
async function apply(next?: LifestyleQuizStep): Promise<boolean> {
  try {
    await putSurveyResponse(LIFESTYLE_SURVEY_KEY, lifestyleQuizResponseBody(definition.value, state.value, steps.value))
    fieldErrors.value = {}
    queryCache.invalidate(['user-lifestyle'])
    queryCache.invalidate(['user-profile'])
    return true
  } catch (error) {
    const errors = surveyFieldErrors(error)
    if (Object.keys(errors).length) {
      fieldErrors.value = errors
      const failed = steps.value.find((item) => errors[item])
      if (failed) step.value = failed
      return false
    }
    // Transport / server failure: the answers stay in the layer, the next step retries the PUT —
    // except before Result, which would claim answers that were never saved.
    console.warn('Lifestyle quiz: the server did not take the answers', error)
    if (lifestyleQuizMayMoveOnAfterFailedSave(next)) return true
    toast.show({ tone: 'warning', message: t('lifestyle_quiz.save_error') })
    return false
  }
}

function move(by: 1 | -1) {
  const index = steps.value.indexOf(step.value)
  const next = steps.value[Math.max(0, Math.min(steps.value.length - 1, index + by))]
  if (next) step.value = next
}

async function leaveStep(by: 1 | -1) {
  if (step.value !== 'welcome' && step.value !== 'result') {
    const index = steps.value.indexOf(step.value)
    const ok = await apply(steps.value[Math.max(0, Math.min(steps.value.length - 1, index + by))])
    if (!ok) return
  }
  move(by)
}

function choose(value: string) {
  const current = question.value
  if (!current) return
  const { [current.id]: _cleared, ...rest } = fieldErrors.value
  fieldErrors.value = rest
  state.value = {
    ...state.value,
    answers: {
      ...state.value.answers,
      [current.id]: toggleLifestyleQuizAnswer(state.value.answers[current.id], value, current.multi),
    },
    // Choosing a card other than Other clears the typed text (R171).
    custom: current.customValue && value !== current.customValue ? '' : state.value.custom,
  }
}

const showCustom = computed(
  () => Boolean(question.value?.customValue) && state.value.answers[question.value!.id]?.[0] === question.value?.customValue,
)

const customError = computed(() => (question.value ? fieldErrors.value[question.value.id] ?? '' : ''))
const noteError = computed(() => (definition.value.note ? fieldErrors.value[definition.value.note.id] ?? '' : ''))

/* ── Result ───────────────────────────────────────────────────────────── */

function labelOf(questionId: string, value: string): string {
  const current = definition.value.questions.find((item) => item.id === questionId)
  return current?.options.find((option) => option.value === value)?.label ?? value
}

function resultLabel(questionId: string, title: string): string {
  const key = `lifestyle_quiz.result.${questionId}`
  return te(key) ? t(key) : title
}

const resultRows = computed<FairQuizSummaryRow[]>(() => {
  const rows: FairQuizSummaryRow[] = definition.value.questions
    .filter((item) => steps.value.includes(item.id))
    .map((item) => {
      const chosen = state.value.answers[item.id] ?? []
      const value =
        item.customValue && chosen[0] === item.customValue && state.value.custom.trim()
          ? state.value.custom.trim()
          : chosen.map((option) => labelOf(item.id, option)).join(', ')
      return { key: item.id, label: resultLabel(item.id, item.title), value }
    })
  rows.push({ key: 'note', label: t('lifestyle_quiz.result.note'), value: state.value.note.trim() })
  return rows
})

async function openStyle() {
  store.close()
  await settleFairQuizHistory()
  await router.push(PROFILE_MY_STYLE_ROUTE)
}

/* ── Header + leaving ─────────────────────────────────────────────────── */

const progress = computed(() => lifestyleQuizProgress(steps.value, step.value))
const headerRight = computed(() => (step.value === 'welcome' || step.value === 'result' ? 'none' : 'skip'))
const leaveOpen = ref(false)

function requestClose() {
  if (step.value === 'welcome' || step.value === 'result') {
    store.close()
    return
  }
  leaveOpen.value = true
}

async function exit() {
  leaveOpen.value = false
  await apply()
  store.close()
}

/* ── Open ─────────────────────────────────────────────────────────────── */

watch(
  () => store.session,
  async () => {
    step.value = 'welcome'
    fieldErrors.value = {}
    await Promise.all([definitionQuery.refresh(), lifestyleQuery.refresh(true)])
    state.value = lifestyleQuizStateFrom(definition.value, lifestyleQuery.data.value)
  },
  { immediate: true },
)
</script>

<template>
  <FairQuizLayer
    :open="store.isOpen"
    class="lifestyle-quiz"
    :aria-label="t('lifestyle_quiz.aria')"
    @close-request="requestClose"
  >
    <FairQuizHeader
      :progress="progress"
      :right="headerRight"
      :skip-label="t('quiz.skip')"
      :close-label="t('quiz.close')"
      :progress-label="t('quiz.progress')"
      @close="requestClose"
      @skip="leaveStep(1)"
    />

    <FairQuizWelcome
      v-if="step === 'welcome'"
      :title="t('lifestyle_quiz.welcome.title')"
      :subtitle="t('lifestyle_quiz.welcome.subtitle')"
      :pills="[{ label: t('lifestyle_quiz.welcome.pill_time'), star: false }]"
      :continue-label="t('quiz.continue')"
      :continue-loading="!ready"
      @continue="move(1)"
    />

    <!-- 01–06 Theme card questions -->
    <FairQuizStep
      v-else-if="question"
      fill
      :title="question.title"
      :subtitle="question.subtitle || (question.multi ? t('lifestyle_quiz.pick_any') : t('lifestyle_quiz.pick_one'))"
      :show-back="step !== firstQuestionId"
      :back-label="t('quiz.back')"
      :continue-label="t('quiz.continue')"
      @back="leaveStep(-1)"
      @continue="leaveStep(1)"
    >
      <div class="lifestyle-quiz__content">
        <div
          class="lifestyle-quiz__cards"
          :class="{ 'lifestyle-quiz__cards--icons': question.options.some((option) => option.icon) }"
          :role="question.multi ? 'group' : 'radiogroup'"
          :aria-label="question.title"
        >
          <FairThemeCard
            v-for="option in question.options"
            :key="option.value"
            :label="option.label"
            :icon="option.icon"
            :multi="question.multi"
            :selected="(state.answers[question.id] ?? []).includes(option.value)"
            @click="choose(option.value)"
          />
        </div>
        <FairInputText
          v-if="showCustom"
          v-model="state.custom"
          variant="underline"
          :placeholder="question.customPlaceholder"
          :invalid="Boolean(customError)"
          :helper-text="customError"
          :show-helper="Boolean(customError)"
        />
      </div>
    </FairQuizStep>

    <!-- 07 Note -->
    <FairQuizStep
      v-else-if="step === 'note'"
      fill
      :title="t('lifestyle_quiz.note.title')"
      :subtitle="t('lifestyle_quiz.note.subtitle')"
      :back-label="t('quiz.back')"
      :continue-label="t('lifestyle_quiz.note.done')"
      @back="leaveStep(-1)"
      @continue="leaveStep(1)"
    >
      <FairInputText
        v-model="state.note"
        variant="underline"
        :placeholder="t('lifestyle_quiz.note.placeholder')"
        :invalid="Boolean(noteError)"
        :helper-text="noteError"
        :show-helper="Boolean(noteError)"
      />
    </FairQuizStep>

    <!-- 08 Result: plain text, no AI mark -->
    <FairQuizStep
      v-else-if="step === 'result'"
      :title="t('lifestyle_quiz.result.title')"
      :subtitle="t('lifestyle_quiz.result.subtitle')"
      :show-back="false"
      :continue-label="t('lifestyle_quiz.result.open_style')"
      @continue="openStyle"
    >
      <FairQuizSummary :rows="resultRows" />
    </FairQuizStep>

    <FairQuizLeaveDialog
      v-model:visible="leaveOpen"
      :title="t('lifestyle_quiz.leave.title')"
      :text="t('lifestyle_quiz.leave.text')"
      :keep-label="t('lifestyle_quiz.leave.keep')"
      :exit-label="t('lifestyle_quiz.leave.exit')"
      :close-label="t('quiz.close')"
      @exit="exit"
    />
  </FairQuizLayer>
</template>
